/* Copyright (c) 2020-present CloudIO Inc */

// Copy this file to src/componentregistry/components/Page folder
// and import helpRenders into PageManager.ts file
// import { helpRenders } from './Page_Help';

import { ComponentSample, Item } from 'componentregistry';

const page: {
  root: Item<'Page'>;
  flexPanel: Item<'FlexPanel'>;
  flexItem: Item<'FlexItem'>;
  box: Item<'Box'>;
  typography: Item<'Typography'>;
  triggerOnClick: Item<'TriggerOnClick'>;
  showMessage: Item<'ActionShowMessage'>;
  box1: Item<'Box'>;
} = {
  box: {
    children: ['typography', 'triggerOnClick'],
    itemId: 'box',
    parent: 'box1',
    props: {
      cssClassName: {
        type: 'text',
        value: 'btn btn-primary',
      },
    },
    type: 'Box',
  },
  box1: {
    children: ['box'],
    itemId: 'box1',
    parent: 'flexItem',
    props: {
      boxHeight: '1',
      boxWidth: '1',
    },
    type: 'Box',
  },
  flexItem: {
    children: ['box1'],
    itemId: 'flexItem',
    parent: 'flexPanel',
    props: {
      cssClassName: {
        type: 'text',
        value: 'bs-css-root',
      },
    },
    type: 'FlexItem',
  },
  flexPanel: {
    children: ['flexItem'],
    itemId: 'flexPanel',
    parent: 'root',
    props: {},
    type: 'FlexPanel',
  },
  root: {
    children: ['flexPanel'],
    itemId: 'root',
    parent: 'root',
    props: {
      windowTitle: {
        type: 'text',
        value: 'Blank Page',
      },
      withBootstrap: true,
    },
    type: 'Page',
  },
  showMessage: {
    children: [],
    itemId: 'showMessage',
    parent: 'triggerOnClick',
    props: {
      message: {
        type: 'text',
        value: 'Hi there!',
      },
      type: 'info',
    },
    type: 'ActionShowMessage',
  },
  triggerOnClick: {
    children: ['showMessage'],
    itemId: 'triggerOnClick',
    parent: 'box',
    props: {},
    type: 'TriggerOnClick',
  },
  typography: {
    children: [],
    itemId: 'typography',
    parent: 'box',
    props: {
      text: {
        type: 'text',
        value: 'Bootstrap CSS Button!',
      },
    },
    type: 'Typography',
  },
};

export const helpRenders: ComponentSample<'Page'>[] = [
  {
    title: 'Page with Bootstrap CSS',
    description: `With Bootstrap prop set to true, the page will have Bootstrap CSS classes. In this example, \`btn btn-primary\` class is added to the Box component with \`bs-css-root\` class added to the Flex Item component (Box's parent).
      
\`\`\`alert_warning
Note: Once this prop is set to true, it will not unload the css when it's set back to false. If you want to remove the Bootstrap CSS, you need to refresh the browser.
\`\`\`

\`\`\`alert_success
Enabling Bootstrap CSS will not affect the standard components. You can still use the standard components without any issues, but they are styled with material design!.
\`\`\`
`,
    page,
    exampleId: 'eg-01GGFZJ8FB3AJPK6YKZGB14JAH',
    clearParentChildren: true,
    height: 280,
    visibleProps: ['withBootstrap'],
    item: {
      type: 'Page',
      props: {
        windowTitle: {
          type: 'text',
          value: '',
        },
        withBootstrap: true,
      },
      itemId: 'root',
      parent: 'root',
      children: ['flexPanel'],
    },
  },
];

const twpage: {
  box: Item<'Box'>;
  box1: Item<'Box'>;
  root: Item<'Page'>;
  flexItem: Item<'FlexItem'>;
  flexPanel: Item<'FlexPanel'>;
  typography: Item<'Typography'>;
  showMessage: Item<'ActionShowMessage'>;
  triggerOnClick: Item<'TriggerOnClick'>;
} = {
  box: {
    children: ['typography', 'triggerOnClick'],
    itemId: 'box',
    parent: 'box1',
    props: {
      cssClassName: {
        type: 'text',
        value:
          'inline-block px-6 py-2.5 bg-blue-600 text-white font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out',
      },
    },
    type: 'Box',
  },
  box1: {
    children: ['box'],
    itemId: 'box1',
    parent: 'flexItem',
    props: {
      boxHeight: '1',
      boxWidth: '1',
    },
    type: 'Box',
  },
  flexItem: {
    children: ['box1'],
    itemId: 'flexItem',
    parent: 'flexPanel',
    props: {
      cssClassName: {
        type: 'text',
        value: 'tw-css-root',
      },
    },
    type: 'FlexItem',
  },
  flexPanel: {
    children: ['flexItem'],
    itemId: 'flexPanel',
    parent: 'root',
    props: {},
    type: 'FlexPanel',
  },
  root: {
    children: ['flexPanel'],
    itemId: 'root',
    parent: 'root',
    props: {
      windowTitle: {
        type: 'text',
        value: 'Blank Page',
      },
      withTailwind: true,
    },
    type: 'Page',
  },
  showMessage: {
    children: [],
    itemId: 'showMessage',
    parent: 'triggerOnClick',
    props: {
      message: {
        type: 'text',
        value: 'Hi there!',
      },
      type: 'info',
    },
    type: 'ActionShowMessage',
  },
  triggerOnClick: {
    children: ['showMessage'],
    itemId: 'triggerOnClick',
    parent: 'box',
    props: {},
    type: 'TriggerOnClick',
  },
  typography: {
    children: [],
    itemId: 'typography',
    parent: 'box',
    props: {
      text: {
        type: 'text',
        value: 'Tailwind CSS Button!',
      },
    },
    type: 'Typography',
  },
};

export const twhelpRenders: ComponentSample<'Page'>[] = [
  {
    title: 'Page with Tailwind CSS',
    description: `With Tailwind prop set to true, the page will have Tailwind CSS classes. In this example, the following classes are added to the Box component with \`tw-css-root\` class added to the Flex Item component (Box's parent). 
    
\`\`\`css
inline-block px-6 py-2.5 bg-blue-600 text-white font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out
\`\`\`

\`\`\`alert_warning
Note: Once this prop is set to true, it will not unload the css when it's set back to false. If you want to remove the Tailwind CSS, you need to refresh the browser.
\`\`\`

\`\`\`alert_success
Enabling Tailwind CSS will not affect the standard components. You can still use the standard components without any issues, but they are styled with material design!.
\`\`\`
`,
    page: twpage,
    exampleId: 'eg-01GGG0XSA8WV41PJCAFMXW5T7F',
    clearParentChildren: true,
    height: 280,
    visibleProps: ['withTailwind'],
    item: {
      type: 'Page',
      props: {
        windowTitle: {
          type: 'text',
          value: '',
        },
        withTailwind: true,
      },
      itemId: 'root',
      parent: 'root',
      children: ['flexPanel'],
    },
  },
];
